import AARDatePicker from "@/components/AARDatePicker";
import AARTextArea from "@/components/AARTextArea";
import AARTextField from "@/components/AARTextField";
import BaseDrawer from "@/components/BaseDrawer";
import { AVAILABLE } from "@/constants/area";
import { RELEASED } from "@/constants/request";
import { ENGINEER, PROJECT_ADMIN, SUPER_ADMIN } from "@/constants/roles";
import { REJECTED } from "@/constants/tasks";
import { IArea, ILevel } from "@/interfaces/level.interface";
import { IRequest } from "@/interfaces/request.interface";
import { ITaskAction } from "@/interfaces/task.interface";
import { reqSvc } from "@/services/request.service";
import { showLoader } from "@/store/slices/loaderSlice";
import { store } from "@/store/store";
import { isAuthorize } from "@/utils/common";
import { Button } from "@progress/kendo-react-buttons";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import {
  Download,
  EyeIcon,
  Paperclip
} from "lucide-react";
import { useEffect, useState } from "react";
import "react-image-crop/dist/ReactCrop.css";
import AssignmentsGrid from "./AssignmentsGrid";

interface IProps {
  setIsViewMode: React.Dispatch<React.SetStateAction<boolean>>;
  readOnlyMode: boolean;
  handlePreview: any;
  showDetailsDrawer: boolean;
  setShowDetailsDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showSubmissionDrawer: boolean;
  setShowSubmissionDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showAssignmentsDrawer: boolean;
  setShowAssignmentsDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  selectedArea?: IArea;
  selectedRequest?: IRequest | null;
  selectedLevel: ILevel | null;
  handleAction: (data: ITaskAction) => void;
  imagePreviewWidth?: string;
  imagePreviewHeight?: string;
}

function DetailsDrawer({
  setIsViewMode,
  readOnlyMode,
  showDetailsDrawer,
  handlePreview,
  setShowDetailsDrawer,
  setShowSubmissionDrawer,
  showSubmissionDrawer,
  showAssignmentsDrawer,
  setShowAssignmentsDrawer,
  selectedArea,
  selectedRequest,
  selectedLevel,
  handleAction,
  imagePreviewWidth,
  imagePreviewHeight,
  ...drawerProps
}: IProps) {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (showDetailsDrawer) {
      setActiveTab(0);
    }
  }, [showDetailsDrawer]);

  const renderDrawerContent = () => (
    <>
      <div className="grid gap-4 -mt-2 tabs-section">
        <TabStrip
          tabPosition={"top"}
          selected={activeTab}
          onSelect={(e) => setActiveTab(e.selected)}
        >
          <TabStripTab
            title={
              <span className="text-txtPrimary inline-flex items-center gap-2">
                <img src="./images/details-icon.png" alt="" />
                Details
              </span>
            }
            contentClassName="w-full"
          >
            <div className="border border-[#E0E5F2] p-4 w-full mt-4">
              <div className="grid grid-cols-2 w-full gap-x-3 gap-y-5">
                <AARTextField
                  name="name"
                  value={selectedLevel?.name}
                  label="Level"
                  placeholder=""
                  disabled={true}
                  required={false}
                  onValidate={() => {}}
                  onChange={() => {}}
                />
                <AARTextField
                  name="name"
                  value={selectedArea?.name}
                  label="Area"
                  placeholder=""
                  disabled={true}
                  required={false}
                  onValidate={() => {}}
                  onChange={() => {}}
                />

                {selectedRequest && (
                  <>
                    <div className="col-span-2 grid grid-cols-2 gap-x-2">
                      <AARTextField
                        name="wirNo"
                        value={selectedRequest?.wirNumber}
                        label="WIR No."
                        placeholder=""
                        disabled={true}
                        required={false}
                        onValidate={() => {}}
                        onChange={() => {}}
                      />
                      <div className="grid grid-cols-2 gap-x-2">
                        <AARDatePicker
                          name="from"
                          value={
                            selectedRequest?.from
                              ? new Date(selectedRequest?.from)
                              : null
                          }
                          label="From"
                          placeholder=""
                          disabled={true}
                          required={false}
                          time={false}
                        />
                        <AARDatePicker
                          name="to"
                          value={
                            selectedRequest?.to
                              ? new Date(selectedRequest?.to)
                              : null
                          }
                          label="To"
                          placeholder=""
                          disabled={true}
                          required={false}
                          time={false}
                        />
                      </div>
                    </div>

                    <div className="col-span-2">
                      <AARTextArea
                        name="workSummary"
                        value={selectedRequest?.workSummary}
                        label="Work Summary"
                        required={true}
                        placeholder="Enter Work Summary"
                        disabled={true}
                        className="col-span-2"
                        onValidate={() => {}}
                        onChange={() => {}}
                      />
                    </div>
                    <div className="col-span-2">
                      <AARTextArea
                        name="workSummary"
                        value={selectedRequest?.description}
                        label="Description"
                        required={true}
                        placeholder="Enter Work Summary"
                        disabled={true}
                        className="col-span-2"
                        onChange={() => {}}
                        onValidate={() => {}}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="mt-4">
              {selectedRequest && (
                <AssignmentsGrid
                  setIsViewMode={setIsViewMode}
                  readOnlyMode={readOnlyMode}
                  handleAction={handleAction}
                  request={selectedRequest}
                />
              )}
            </div>
          </TabStripTab>
          {selectedRequest && (
            <TabStripTab
              title={
                <span className="text-txtPrimary inline-flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments
                </span>
              }
            >
              {selectedRequest?.attachmentUrls?.length ? (
                <div className="grid grid-cols-6 gap-3 mt-4">
                  {selectedRequest?.attachmentUrls.map((file) => {
                    // Function to detect file type from URL
                    const getFileType = (url: any) => {
                      const extension = url.split(".").pop().toLowerCase();
                      if (extension === "pdf") {
                        return "pdf";
                      } else if (["xls", "xlsx", "csv"].includes(extension)) {
                        return "excel";
                      } else if (
                        ["jpg", "jpeg", "png", "gif", "webp"].includes(
                          extension
                        )
                      ) {
                        return "image";
                      } else {
                        return "unknown";
                      }
                    };

                    const fileType = getFileType(file);

                    if (fileType === "pdf" || fileType === "excel") {
                      return (
                        <div
                          key={file}
                          className="relative border rounded-md overflow-hidden group w-[100%] h-[150px] bg-gray-100 flex items-center justify-center"
                        >
                          {fileType === "pdf" ? (
                            <img
                              src="./images/pdf.png"
                              alt="PDF Icon"
                              className="w-20 h-14"
                            />
                          ) : (
                            <img
                              src="./images/excel.png"
                              alt="PDF Icon"
                              className="w-20 h-14"
                            />
                          )}

                          <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-600 truncate px-2">
                            {file.split("/").pop()}
                          </div>

                          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                            {/* <Button
                            className="rounded p-2 text-white border border-white"
                            fillMode={"outline"}
                            onClick={() => handlePreview(file)}
                          >
                            <EyeIcon size={16} className="w-4 h-4" />
                          </Button> */}
                            <Button
                              className="rounded p-2 text-white border border-white"
                              fillMode={"outline"}
                              onClick={() => {
                                // Handle file download here
                                const link = document.createElement("a");
                                link.href = file;
                                link.download = file.split("/").pop() || "file";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              <Download size={16} className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    }

                    // Default image rendering (existing code)
                    return (
                      <div
                        key={file}
                        className="relative border rounded-md overflow-hidden group w-[100%] h-[150px]"
                      >
                        <img
                          src={file}
                          className="object-cover h-full w-full"
                        />
                        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                          <Button
                            className="rounded p-2 text-white border border-white"
                            fillMode={"outline"}
                            onClick={() => handlePreview(file)}
                          >
                            <EyeIcon size={16} className="w-4 h-4" />
                          </Button>
                          <Button
                            className="rounded p-2 text-white border border-white"
                            fillMode={"outline"}
                            onClick={() => {
                              // Handle file download here
                              const link = document.createElement("a");
                              link.href = file;
                              link.download = file.split("/").pop() || "file";
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            <Download size={16} className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <></>
              )}
            </TabStripTab>
          )}
        </TabStrip>
      </div>
    </>
  );

  const handleRejectReassign = () => {
    store.dispatch(showLoader());
    if (selectedRequest?.id) {
      reqSvc.reassignRejectedAAR(selectedRequest.id).then((res) => {
        if (res.error) {
          return;
        }
        setShowDetailsDrawer(false);
        setShowAssignmentsDrawer(true);
      });
    }
  }

  return (
    <BaseDrawer
      isOpen={showDetailsDrawer}
      showIcon={true}
      onClose={() => setShowDetailsDrawer(false)}
      title={selectedArea?.name ? `${selectedArea?.name}` : "Area Submission"}
      subtitle={selectedRequest?.refNo}
      primaryActionText={"Assign"}
      primaryActionDisabled={false}
      showBackButton={true}
      onBackButtonClick={() => {
        setShowAssignmentsDrawer(true);
        setShowDetailsDrawer(false);
      }}
      onPrimaryAction={() => {
        setShowDetailsDrawer(false);
        setShowSubmissionDrawer(!showSubmissionDrawer);
      }}
      secondaryActionText={
        selectedRequest?.status === AVAILABLE ||
        selectedRequest?.status === RELEASED
          ? "Cancel"
          : "Close"
      }
      onSecondaryAction={() => {
        setShowDetailsDrawer(false);
        setShowAssignmentsDrawer(true);
      }}
      showPrimaryAction={
        (!selectedRequest ||
          selectedRequest?.status === AVAILABLE ||
          selectedRequest?.status === RELEASED) &&
        isAuthorize([SUPER_ADMIN, PROJECT_ADMIN, ENGINEER])
      }
      showCustomAction={isAuthorize([ENGINEER, PROJECT_ADMIN]) && selectedRequest?.status === REJECTED}
      onCustomAction={handleRejectReassign}
      customActionText="Reassign Rejected AAR"
      maxW={"70vw"}
      status={selectedRequest?.status}
      {...drawerProps}
    >
      {renderDrawerContent()}
    </BaseDrawer>
  );
}

export default DetailsDrawer;
